import { getBackend } from '@/backend';
import { logger } from '@/utils/logger';
import type {
    BackendClients,
    CreateProjectOptions,
    ProjectCatalogClient,
    ProjectCloudSnapshot,
    ProjectDataResourceKey,
    ProjectDataSubscriptionUpdate,
    ProjectCreateData,
} from '@/backend/contracts';
import type { ProjectCommunicationAudienceConfig } from '../email/audience';
import type { ProjectCommunicationSettings } from '../email/communication-settings';
import { normalizeProjectCommunicationAudiences } from './project-communication-normalizers';
import type { ProjectScope } from './project-scope';
import { getProjectSnapshot, subscribeProjectData } from './project-reader';

export { normalizeProjectCommunicationAudiences };
export type { CloudProjectMeta } from '../../store/cloud-types';

type ProjectCatalogBackend = Pick<BackendClients, 'projectCatalog'>;

function getProjectCatalogClient(
    clients: ProjectCatalogBackend = getBackend(),
): ProjectCatalogClient {
    if (!clients.projectCatalog) {
        throw new Error('ProjectCatalogClient is not configured for the active backend.');
    }
    return clients.projectCatalog;
}

/**
 * Compatibility facade for legacy callers while project reads move behind
 * ProjectDataClient.
 */
export async function getCloudProject(scope: ProjectScope): Promise<ProjectCloudSnapshot> {
    try {
        return await getProjectSnapshot(scope);
    } catch (error: unknown) {
        logger.error('Error loading project:', error);
        throw error;
    }
}

/**
 * Compatibility facade that preserves the legacy root/resource callback shape.
 */
export function subscribeToProject(
    scope: ProjectScope,
    resources: string[],
    onUpdate: (data: ProjectDataSubscriptionUpdate, resourceId?: string) => void,
): () => void {
    return subscribeProjectData(
        scope,
        { resources: resources as readonly ProjectDataResourceKey[] },
        ({ update, resourceId }) => {
            if (resourceId) {
                onUpdate(update, resourceId);
                return;
            }
            onUpdate(update);
        },
    );
}

export function createCloudProject(
    userId: string,
    projectData: ProjectCreateData,
    options?: CreateProjectOptions,
): Promise<string> {
    return getProjectCatalogClient().createProject(userId, projectData, options);
}

export function getUserProjects(userId: string) {
    return getProjectCatalogClient().listUserProjects(userId);
}

export function deleteCloudProject(scope: ProjectScope): Promise<void> {
    return getProjectCatalogClient().deleteProject(scope);
}

export function archiveProject(scope: ProjectScope): Promise<void> {
    return getProjectCatalogClient().archiveProject(scope);
}

export function unarchiveProject(scope: ProjectScope): Promise<void> {
    return getProjectCatalogClient().unarchiveProject(scope);
}

export function updateProjectClosingDate(
    scope: ProjectScope,
    closingDate: string | null,
): Promise<void> {
    return getProjectCatalogClient().updateProjectClosingDate(scope, closingDate);
}

export function updateProjectCommunicationAudiences(
    scope: ProjectScope,
    config: ProjectCommunicationAudienceConfig,
): Promise<void> {
    return getProjectCatalogClient().updateProjectCommunicationAudiences(scope, config);
}

export function updateProjectCommunicationSettings(
    scope: ProjectScope,
    settings: ProjectCommunicationSettings,
): Promise<void> {
    return getProjectCatalogClient().updateProjectCommunicationSettings(scope, settings);
}
